import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { findCategory, categoryVisible } from '@/config/adminNav';

/**
 * Contextual "back to category" bar. Rendered by Layout above every page.
 * Shows only when the current URL carries `?from=<categoryId>` (added by the
 * Administration category page when deep-linking into a function), so a user
 * three layers deep (Administration → Category → Function) can return to the
 * category they came from. No-ops on every other page.
 */
export default function AdminReturnBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  const from = new URLSearchParams(location.search).get('from');
  if (!from) return null;
  const category = findCategory(from);
  if (!category || !categoryVisible(category, user, hasPermission)) return null;
  const hubPath = category.audience === 'platform_console' ? '/platform-console' : '/administration';

  return (
    <div
      className="sticky top-0 z-20 flex items-center gap-1.5 px-6 py-2 bg-card/80 backdrop-blur border-b border-border/40"
      data-testid="admin-return-bar"
    >
      <button
        onClick={() => navigate(`${hubPath}/${from}`)}
        className="flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors"
        data-testid="admin-return-bar-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back to {category.label}
      </button>
    </div>
  );
}
